import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { BRAND_CLASSES } from '@/constants/theme';
/**
 * Common MultiSelect component for selecting multiple items via checkboxes
 * Does NOT use React Hook Form - manages its own state via value/onChange
 *
 * @example
 * ```tsx
 * const [selectedIds, setSelectedIds] = useState<string[]>([])
 *
 * <MultiSelect
 *   label="Audiences"
 *   options={audiences.map(a => ({ value: a.Id, label: a.Name }))}
 *   value={selectedIds}
 *   onChange={setSelectedIds}
 *   isLoading={isLoading}
 *   helperText="Select multiple to aggregate contacts"
 *   emptyMessage="No audiences yet."
 *   emptyAction={{
 *     label: 'Create one',
 *     onClick: handleCreateAudience
 *   }}
 * />
 * ```
 */
export function MultiSelect({ label, required, error, helperText, options, value, onChange, isLoading, containerClassName, emptyMessage = 'No options available', emptyAction, maxHeight = '10rem' }) {
    const handleToggle = (optionValue) => {
        const isSelected = value.includes(optionValue);
        if (isSelected) {
            onChange(value.filter(v => v !== optionValue));
        }
        else {
            onChange([...value, optionValue]);
        }
    };
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { className: `block text-sm font-medium mb-2 ${BRAND_CLASSES.INPUT.LABEL}`, children: [label, required && _jsx("span", { className: BRAND_CLASSES.INPUT.REQUIRED, children: " *" })] })), _jsxs("div", { className: "space-y-2", children: [isLoading && (_jsx("div", { className: `text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: "Loading..." })), !isLoading && options.length === 0 && (_jsxs("div", { className: `text-xs ${BRAND_CLASSES.INPUT.HELPER} flex items-center gap-2`, children: [_jsx("span", { children: emptyMessage }), emptyAction && (_jsx("button", { type: "button", onClick: emptyAction.onClick, className: "text-primary-600 dark:text-primary-400 hover:underline font-medium", children: emptyAction.label }))] })), !isLoading && options.length > 0 && (_jsx("div", { className: `
                            border rounded-lg p-2 overflow-auto space-y-1
                            ${error ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'}
                            ${BRAND_CLASSES.INPUT.NORMAL_BG}
                        `, style: { maxHeight }, children: options.map(option => {
                            const isChecked = value.includes(option.value);
                            return (_jsxs("label", { className: "flex items-center gap-2 text-xs cursor-pointer select-none text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 rounded px-1 py-0.5 transition-colors", children: [_jsx("input", { type: "checkbox", className: "rounded border-slate-300 dark:border-slate-500 text-primary-600 focus:ring-primary-500", checked: isChecked, onChange: () => handleToggle(option.value) }), _jsx("span", { className: "truncate", children: option.label })] }, option.value));
                        }) })), error && (_jsx("p", { className: `text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), !error && helperText && (_jsx("p", { className: `text-[11px] ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] })] }));
}
